import { Answers, Task, ValidationType, WorkModel } from 'models/task.model';
import {
  DraftPredefinedOptionsValidation,
  DraftTaskValidationField,
} from './task.type';

export const TASK_TITLE_CHARACTER_LIMIT = 100;
export const TASK_DESCRIPTION_CHARACTER_LIMIT = 900;
export const TASK_ERROR_MESSAGE_CHARACTER_LIMIT = 300;
export const TASK_COMMENT_CHARACTER_LIMIT = 300;
export const TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT = 300;

export const validateTitle = (
  title: string,
  fullValidation = false,
): string => {
  if (fullValidation && !title.length) {
    return 'Title is required';
  }

  if (title.length > TASK_TITLE_CHARACTER_LIMIT) {
    return `Title must be less than ${TASK_TITLE_CHARACTER_LIMIT} characters long. You are ${
      title.length - TASK_TITLE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateDescription = (
  description: string,
  fullValidation = false,
): string => {
  if (fullValidation && !description.length) {
    return 'Description is required';
  }

  if (description.length > TASK_DESCRIPTION_CHARACTER_LIMIT) {
    return `Description must be less than ${TASK_DESCRIPTION_CHARACTER_LIMIT} characters long. You are ${
      description.length - TASK_DESCRIPTION_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateWorkModels = (
  workModels: WorkModel[],
  fullValidation = false,
): string => {
  if (fullValidation && !workModels.length) {
    return 'Type is required';
  }

  return '';
};

export const validateAnswer = (
  validator: ValidationType,
  answers: Answers,
  fullValidation = false,
): string => {
  const isContainsAnswerPresent = Boolean(answers.containsOneOf.length);
  const isMatchesAnswerPresent = Boolean(answers.matchesOneOf.length);
  const isPredefinedQuestionPresent = Boolean(answers.predefinedOptions);

  if (
    fullValidation &&
    ((validator === 'CONTAINS_ONE_OF' &&
      !isContainsAnswerPresent &&
      !isPredefinedQuestionPresent) ||
      (validator === 'MATCHES_ONE_OF' &&
        !isMatchesAnswerPresent &&
        !isPredefinedQuestionPresent))
  ) {
    return 'At least 1 answer is required';
  }

  return '';
};

export const validateErrorMessage = (errorMessage: string): string => {
  if (errorMessage.length > TASK_ERROR_MESSAGE_CHARACTER_LIMIT) {
    return `Error message must be less than ${TASK_ERROR_MESSAGE_CHARACTER_LIMIT} characters long. You are ${
      errorMessage.length - TASK_ERROR_MESSAGE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateSuccessMessage = (successMessage: string): string => {
  if (successMessage?.length > TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT) {
    return `Success message must be less than ${TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT} characters long. You are ${
      successMessage.length - TASK_SUCCESS_MESSAGE_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateComment = (comment: string): string => {
  if (comment.length > TASK_COMMENT_CHARACTER_LIMIT) {
    return `Comment must be less than ${TASK_COMMENT_CHARACTER_LIMIT} characters long. You are ${
      comment.length - TASK_COMMENT_CHARACTER_LIMIT
    } characters over`;
  }

  return '';
};

export const validateTask = (
  task: Task,
  fullValidation = true,
): DraftTaskValidationField => {
  const {
    title,
    description,
    workModels,
    validator,
    answers,
    errorMessage,
    successMessage,
    comment,
  } = task;

  return {
    title: validateTitle(title, fullValidation),
    description: validateDescription(description, fullValidation),
    workModels: validateWorkModels(workModels, fullValidation),
    answers: validateAnswer(validator, answers, fullValidation),
    errorMessage: validateErrorMessage(errorMessage),
    successMessage: validateSuccessMessage(successMessage),
    comment: validateComment(comment),
  };
};

export const validatePredefinedOption = (
  predefinedOptions: Answers['predefinedOptions'],
  fullValidation = true,
): DraftPredefinedOptionsValidation => {
  const noError: DraftPredefinedOptionsValidation = {
    predefinedOptionCorrectAnswer: '',
    predefinedOptionIncorrectAnswer: '',
  };

  if (!fullValidation || !predefinedOptions) {
    return noError;
  }
  const { answers } = predefinedOptions;

  const correctAnswers = answers.filter((a) => a.isCorrect);

  if (correctAnswers.length < 1) {
    noError.predefinedOptionCorrectAnswer =
      'Should be at least 1 correct answer';
  }
  const incorrectAnswers = answers.filter((a) => !a.isCorrect);

  if (incorrectAnswers.length < 1) {
    noError.predefinedOptionIncorrectAnswer =
      'Should be at least 1 incorrect answer';
  }

  return noError;
};
